// Workspace audit log: who changed what, and when.
//
// Search wiring as in surveys.index.tsx (usePageSearch, kore SearchBar,
// useCursorStack). `?eventId=` focuses an event in kore's AuditSheet, so a
// sheet is deep-linkable; HistorySection links here with it.

import { DataTable, type DataTableColumn, SearchBar, toast, useKoreSearch } from "@pej/kore/components";
import type { AuditEvent } from "@pej/kore/types";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useMemo } from "react";
import { z } from "zod";
import { type AuditQueryField, type AuditSortField, useAuditSearch } from "@/api/audit";
import { IndexPage } from "@/components/index-page/index-page";
import { TABLE_LABELS, tableError } from "@/components/page-states";
import { AuditEventSheet } from "@/features/audit/audit-event-sheet";
import {
    AUDIT_DEFAULT_ORDER,
    AUDIT_PAGE_SIZE_OPTIONS,
    AUDIT_QUERY_FIELDS,
    AUDIT_SEARCH_MANIFEST,
    AUDIT_SORT_FIELDS,
    type AuditSearchBarFilters,
    auditFiltersForWire,
    useAuditFacetLoader,
} from "@/features/audit/audit-filters";
import { ActionCell, ActorCell, DetailsCell, TargetCell, TargetTypeCell, TimeCell } from "@/features/audit/audit-row";
import { toKoreAuditEvent } from "@/features/audit/kore-audit-adapter";
import { usePageSearch } from "@/features/search/page-search";
import { sortableHeader } from "@/features/search/sortable-header";
import { useCursorStack } from "@/features/search/use-cursor-stack";

// Not coerced: event ids are UUIDs, which JSON.parse leaves as strings.
// `groups`, `targetTypes` and `targetIds` are kore filter keys, declared so
// History can link its full trail; kore reads them from the URL like any filter.
export const auditSearchSchema = z.object({
    eventId: z.string().optional(),
    groups: z.string().optional(),
    targetTypes: z.string().optional(),
    targetIds: z.string().optional(),
});

export const Route = createFileRoute("/w/$workspaceId/audit")({
    validateSearch: auditSearchSchema,
    component: AuditPage,
});

function AuditPage() {
    const { workspaceId } = Route.useParams();
    const navigate = useNavigate();
    const { eventId } = Route.useSearch();
    const page = usePageSearch<AuditSortField, AuditQueryField>({
        sortFields: AUDIT_SORT_FIELDS,
        defaultOrder: AUDIT_DEFAULT_ORDER,
        queryFields: AUDIT_QUERY_FIELDS,
        pageSizeOptions: AUDIT_PAGE_SIZE_OPTIONS,
    });

    const search = useKoreSearch<AuditSearchBarFilters>({
        manifest: AUDIT_SEARCH_MANIFEST,
        router: { search: page.rawSearch, onChange: page.onSearchPatch },
        orderBy: page.orderBy,
        onOrderByChange: page.onOrderByChange,
        query: page.query,
        onQueryChange: page.setQuery,
        searchBy: page.searchBy,
        onSearchByChange: page.onSearchByChange,
        searchPlaceholder: "Search by actor or target…",
        loadDynamicOptions: useAuditFacetLoader(workspaceId),
    });

    const predicate = page.predicate(auditFiltersForWire(search.rq?.filters));
    const cursor = useCursorStack({ request: predicate });
    const auditQuery = useAuditSearch(workspaceId, { ...predicate, pageToken: cursor.pageToken });
    const pager = cursor.pager(auditQuery.data?.nextPageToken ?? null);
    const rows = useMemo(() => (auditQuery.data?.items ?? []).map(toKoreAuditEvent), [auditQuery.data]);

    // `replace`: opening, drilling and closing the sheet are not history steps.
    const setEventId = useCallback(
        (next: string | null) => {
            void navigate({
                to: ".",
                search: (prev: Record<string, unknown>) => ({ ...prev, eventId: next ?? undefined }),
                replace: true,
            });
        },
        [navigate],
    );
    const closeSheet = useCallback(() => setEventId(null), [setEventId]);
    const onNotFound = useCallback(() => {
        toast.error("Audit event not found");
        setEventId(null);
    }, [setEventId]);

    const isSortable = (key: string) => search.sortableColumnKeys.has(key);
    // `OccurredAt` is the server's sort field name, which kore matches against the column key.
    const columns: DataTableColumn<AuditEvent>[] = [
        {
            key: "OccurredAt",
            header: sortableHeader("Time", isSortable("OccurredAt")),
            sortable: isSortable("OccurredAt"),
            cell: (row) => <TimeCell event={row} />,
        },
        { key: "actor", header: "Actor", cell: (row) => <ActorCell event={row} /> },
        { key: "action", header: "Action", cell: (row) => <ActionCell event={row} /> },
        { key: "targetType", header: "Target type", cell: (row) => <TargetTypeCell event={row} /> },
        { key: "target", header: "Target", cell: (row) => <TargetCell event={row} /> },
        { key: "details", header: "Details", cell: (row) => <DetailsCell event={row} /> },
    ];

    const filtered = page.searchText !== "" || search.filters.length > 0;

    return (
        <IndexPage
            fill
            title="Audit log"
            description="A time-stamped record of every change in this workspace — who changed what, and when."
        >
            <SearchBar {...search.barProps} size="compact" />
            <DataTable
                className="min-h-0 flex-1"
                density="compact"
                caption="Audit log"
                columns={columns}
                rows={rows}
                rowKey={(row) => row.eventId}
                onRowClick={(row) => setEventId(row.eventId)}
                sort={search.tableSortProps.sort}
                onSortChange={search.tableSortProps.onSortChange}
                state={auditQuery.isError ? "error" : auditQuery.isLoading ? "loading" : undefined}
                error={tableError(auditQuery.error, () => void auditQuery.refetch())}
                labels={TABLE_LABELS}
                empty={
                    filtered
                        ? { headline: "No changes match", body: "Try a different search, or clear a filter." }
                        : {
                              headline: "No changes recorded yet",
                              body: "Changes to surveys, placements, templates and security appear here as they're made.",
                          }
                }
                pagination={{
                    ...pager,
                    pageSize: page.pageSize,
                    totalCount: auditQuery.data?.totalCount ?? 0,
                    pageSizeOptions: [...AUDIT_PAGE_SIZE_OPTIONS],
                    onPageSizeChange: page.setPageSize,
                    entityLabel: "event",
                }}
            />
            <AuditEventSheet
                workspaceId={workspaceId}
                eventId={eventId ?? null}
                onNavigate={setEventId}
                onClose={closeSheet}
                onNotFound={onNotFound}
            />
        </IndexPage>
    );
}
